import { useEffect, useRef } from 'react';

import { useNavigate } from '@reach/router';
import { useDispatch, useSelector } from 'react-redux';

import HeadAddons from '../../components/HeadAddons';
import LoadingBar from '../../components/LoadingBar/LoadingBar';
import { convertWorkshopLevelsToRobotAccordion } from '../../components/Pages/AgePage/helpers';
import { useAges } from '../../hooks/useAges';
import { useSummerplayTypes } from '../../hooks/useSummerplayTypes';
import { getWorkshops } from '../../provider/models/workshopsModel';
import { env } from '../../utils/env';
import {
  clearedData,
  downloadedData,
  loadedPage,
  selectedAge,
  selectedCity,
  selectedLocation,
  selectedOccurrence,
  selectedRobot,
  selectedService,
} from '../actions';
import { pages } from '../pageTypes';

export default function PageWrapper({
  pageToLoad,
  shouldBePage,
  serviceId,
  cityId,
  ageId,
  robotId,
  locationId,
  occurrenceId,
  conditions,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const waitingForFetch = useRef(false);

  const clearAndRedirect = (why) => {
    navigate(`${env.PUBLIC_URL}/`);
    dispatch(clearedData('state', 'service'));
    dispatch(clearedData('state', 'city'));
    dispatch(clearedData('state', 'age'));
    dispatch(clearedData('state', 'robot'));
    dispatch(clearedData('state', 'locationId'));
    dispatch(clearedData('state', 'occurrence'));
    console.error(`Wrong ${why} in url.`);
  };

  const currentPage = useSelector((store) => store.state.page);

  const availableServices = useSelector((store) => store.data.services);
  const currentService = useSelector((store) => store.state.service);

  const availableFranchises = useSelector((store) => store.data.franchises);
  const currentFranchise = useSelector((store) => store.state.city?.franchise);

  const { ages } = useAges();
  const currentAge = useSelector((store) => store.state.age);

  let availableRobots = null;
  if (currentFranchise) {
    availableRobots = convertWorkshopLevelsToRobotAccordion(
      currentFranchise.levels,
    );
  }
  const currentRobot = useSelector((store) => store.state.robot);

  const { summerplayTypes } = useSummerplayTypes();

  const availableWorkshops = useSelector((store) => store.data.workshops);
  const mapFilters = useSelector((store) => store.state.map.filters);

  const currentLocationId = useSelector((store) => store.state.locationId);

  const currentOccurrence = useSelector((store) => store.state.occurrence);

  useEffect(() => {
    if (currentPage?.type === pages.contactSingleService.type) {
      getWorkshops({
        serviceValue: serviceId,
        mapFilters: mapFilters,
        levelId: null,
        locationId: locationId,
        franchiseId: cityId,
      }).then((data) => {
        dispatch(downloadedData(data, 'workshopsCurrentLocation'));
      });
    }
    // TODO: refactor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage?.type]);

  const availableWorkshopsCurrentLocation = useSelector(
    (store) => store.data.workshopsCurrentLocation,
  );

  const data = [
    {
      name: 'page',
      condition: !currentPage || currentPage.type !== shouldBePage.type,
      skipLoading: false,
      action: () => dispatch(loadedPage(shouldBePage)),
    },
    {
      name: 'service',
      condition: !currentService || currentService.value !== serviceId,
      skipLoading: false,
      action: () => {
        const service = availableServices.find((s) => s.value === serviceId);
        service
          ? dispatch(selectedService(service))
          : clearAndRedirect('service');
      },
    },
    {
      name: 'franchise',
      condition: !currentFranchise || +currentFranchise.id !== +cityId,
      skipLoading: true,
      action: () => {
        const fr = availableFranchises.find((f) => +f.id === +cityId);

        if (fr) {
          const cityOptionItem = { text: fr.name, value: fr.id, franchise: fr };
          dispatch(selectedCity(cityOptionItem));
        } else {
          clearAndRedirect('franchise');
        }
      },
    },
    {
      name: 'age',
      condition: +ageId !== 0 && (!currentAge || +currentAge.value !== +ageId),
      skipLoading: false,
      action: () => {
        const age = ages.find((a) => +a.age_int === +ageId);
        if (age) {
          const ageOptionItem = { text: age.age_display, value: age.age_int };
          dispatch(selectedAge(ageOptionItem));
        } else {
          clearAndRedirect('age');
        }
      },
    },
    {
      name: 'robot',
      condition: !currentRobot || +currentRobot.id !== +robotId,
      skipLoading: true,
      action: () => {
        if (availableRobots) {
          const robot = availableRobots.find((r) => +r.id === +robotId);
          // robot ? dispatch(selectedRobot(robot)) : clearAndRedirect('level')
          robot && dispatch(selectedRobot(robot));
        }
      },
    },
    {
      name: 'workshops',
      condition: !availableWorkshops,
      skipLoading: true,
      action: () => {
        if (!waitingForFetch.current && currentRobot) {
          waitingForFetch.current = true;
          getWorkshops({
            serviceValue: serviceId,
            mapFilters: mapFilters,
            levelId: currentRobot.id,
            locationId: null,
            franchiseId: cityId,
          }).then((data) => {
            dispatch(downloadedData(data, 'workshops'));
            waitingForFetch.current = false;
          });
        }
      },
    },
    {
      name: 'summerplays',
      condition: !availableWorkshops,
      skipLoading: true,
      action: () => {
        if (!waitingForFetch.current && summerplayTypes.length) {
          waitingForFetch.current = true;
          getWorkshops({
            serviceValue: serviceId,
            mapFilters: mapFilters,
            franchiseId: cityId,
          }).then((data) => {
            const spWithType = data.map((occurrence) => {
              const typeData = summerplayTypes.find(
                (type) => +type.id === +occurrence.summerplay_scenario_id,
              );

              return {
                ...occurrence,
                typeName: typeData?.name,
                typeDescription: typeData?.description,
                typePdf: typeData?.pdf,
              };
            });
            dispatch(downloadedData(spWithType, 'workshops'));
            waitingForFetch.current = false;
          });
        }
      },
    },
    {
      name: 'academies',
      condition: !availableWorkshops,
      skipLoading: true,
      action: () => {
        if (!waitingForFetch.current && summerplayTypes) {
          waitingForFetch.current = true;
          getWorkshops({
            serviceValue: serviceId,
            mapFilters: mapFilters,
            franchiseId: cityId,
          }).then((data) => {
            const academyWithType = data.map((occurrence) => {
              const typeData = summerplayTypes.find(
                (type) => +type.id === +occurrence.summerplay_scenario_id,
              );

              return {
                ...occurrence,
                typeName: typeData?.name,
                typeDescription: typeData?.description,
                typePdf: typeData?.pdf,
              };
            });
            dispatch(downloadedData(academyWithType, 'workshops'));
            waitingForFetch.current = false;
          });
        }
      },
    },
    {
      name: 'location',
      condition: +currentLocationId !== +locationId,
      skipLoading: true,
      action: () => {
        if (availableWorkshops) {
          const locations = availableWorkshops.map(
            (ws) => +ws.workshop_location_id,
          );
          locations.includes(+locationId)
            ? dispatch(selectedLocation(+locationId))
            : clearAndRedirect('location');
        }
      },
    },
    {
      name: 'occurrence',
      condition: !currentOccurrence || +currentOccurrence.id !== +occurrenceId,
      skipLoading: true,
      action: () => {
        if (currentPage?.type === pages.contactSingleService.type) {
          if (availableWorkshopsCurrentLocation) {
            const occurrence = availableWorkshopsCurrentLocation.find(
              (w) => +w.id === +occurrenceId,
            );
            occurrence
              ? dispatch(selectedOccurrence(occurrence))
              : clearAndRedirect('occurrence');
          }
        } else {
          if (availableWorkshops) {
            const occurrence = availableWorkshops.find(
              (w) => +w.id === +occurrenceId,
            );
            occurrence
              ? dispatch(selectedOccurrence(occurrence))
              : clearAndRedirect('occurrence');
          }
        }
      },
    },
  ];

  let selectedData = data.filter((item) => conditions[item.name]);
  const setDataCondition = selectedData.some((item) => {
    return item.skipLoading ? false : item.condition;
  });

  const checkAndDispatch = () => {
    selectedData.forEach((type) => {
      return type.condition ? type.action() : null;
    });
    // console.log(selectedData)
  };

  // useDeepCompareEffect(checkAndDispatch, [currentFranchise, availableWorkshops])
  useEffect(checkAndDispatch);

  return (
    <>
      <HeadAddons />
      {setDataCondition ? <LoadingBar wholePage /> : pageToLoad}
    </>
  );
}
